import { id } from "@instantdb/react";
import { coerce, empty, letIn, nonempty, pipe } from "@uri/gamla";
import { queryOnce, transact, tx, useAuth, useQuery } from "./db.ts";
import "./style.css";
import {
  ErrorMessage,
  formatLastActivity,
  Loading,
  SelectInput,
} from "./uiComponents.tsx";
import { ClipLoader } from "react-spinners";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { t } from "./theme.ts";

import { getApiBase } from "./config.ts";
const apiBase = getApiBase();

const callApi = (
  endpoint: string,
  token: string,
  payload: Record<string, unknown> = {},
) =>
  fetch(apiBase, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint, token, payload }),
  }).then((r) => r.json());

type TokenRecord = {
  id: string;
  name: string;
  createdAt: number;
  revokedAt?: number;
  token: string;
  groups?: { id: string; name: string }[];
};

const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

const TokenRow = (
  { t: token, onRevoke }: { t: TokenRecord; onRevoke: (id: string) => void },
) => {
  const th = t();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(token.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 0",
        borderBottom: `1px solid ${th.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 100,
        }}
      >
        <span style={{ fontWeight: 500, color: th.text }}>
          {token.name}
        </span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {token.groups && token.groups.length > 0
            ? (
              token.groups.map((g) => (
                <span
                  key={g.id}
                  style={{
                    fontSize: 11,
                    padding: "1px 6px",
                    borderRadius: 4,
                    border: `1px solid ${th.border}`,
                    color: th.textDim,
                  }}
                >
                  {g.name}
                </span>
              ))
            )
            : (
              <span style={{ fontSize: 11, color: th.textFaint }}>
                All groups
              </span>
            )}
        </div>
      </div>
      <span
        style={{
          color: th.textFaint,
          fontSize: 12,
          flex: 1,
          textAlign: "right",
        }}
      >
        {new Date(token.createdAt).toLocaleDateString()}
      </span>
      <button
        type="button"
        className="compact-button"
        onClick={handleCopy}
        style={{ fontSize: 12 }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <button
        type="button"
        className="compact-button"
        style={{ fontSize: 12, color: th.danger }}
        onClick={() => onRevoke(token.id)}
      >
        Revoke
      </button>
    </li>
  );
};

const TokenManager = ({
  accountSecret,
  accountGroups = [],
}: {
  accountSecret: string;
  accountGroups?: { id: string; name: string; lastActivityAt?: number }[];
}) => {
  const th = t();
  const [tokens, setTokens] = useState<TokenRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTokenName, setNewTokenName] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [limitToGroups, setLimitToGroups] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");

  const sectionHeaderStyle = {
    fontWeight: 500,
    fontSize: 14,
    color: th.textDim,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  const refreshTokens = useCallback(() => {
    setLoading(true);
    setError("");
    callApi("listTokens", accountSecret)
      .then((res: { ok?: boolean; tokens?: TokenRecord[]; error?: string }) => {
        if (res.ok && res.tokens) setTokens(res.tokens);
        else setError(res.error || "Failed to load tokens");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountSecret]);

  useEffect(refreshTokens, [refreshTokens]);

  const handleCreate = () => {
    if (!newTokenName.trim()) return;
    const payload: Record<string, unknown> = {
      name: newTokenName.trim(),
    };
    if (limitToGroups && selectedGroupIds.length > 0) {
      payload.groupIds = selectedGroupIds;
    }
    callApi("createToken", accountSecret, payload)
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) {
          setNewTokenName("");
          setSelectedGroupIds([]);
          setLimitToGroups(false);
          setShowCreate(false);
          refreshTokens();
        } else setError(res.error || "Failed to create token");
      })
      .catch((e: Error) => setError(e.message));
  };

  const handleRevoke = (tokenId: string) => {
    callApi("revokeToken", accountSecret, { tokenId })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) refreshTokens();
        else setError(res.error || "Failed to revoke token");
      })
      .catch((e: Error) => setError(e.message));
  };

  const active = tokens.filter((t) => !t.revokedAt);
  const revoked = tokens.filter((t) => t.revokedAt);

  return (
    <div>
      <div style={sectionHeaderStyle}>
        Tokens
        <button
          type="button"
          className="compact-button"
          style={{ fontSize: 12, textTransform: "none" }}
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? "Cancel" : "+ New"}
        </button>
      </div>
      {showCreate && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 12,
            padding: 12,
            background: th.cardBg,
            border: `1px solid ${th.border}`,
            borderRadius: 6,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="Token name (e.g. production)"
              value={newTokenName}
              onChange={(e) =>
                setNewTokenName((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              className="input-field"
              style={{ flex: 1, marginBottom: 0 }}
            />
            <button
              type="button"
              className="compact-button"
              onClick={handleCreate}
            >
              Create
            </button>
          </div>

          {accountGroups.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                fontSize: 13,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: th.text,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={limitToGroups}
                  onChange={(e) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    setLimitToGroups(checked);
                    if (!checked) setSelectedGroupIds([]);
                  }}
                />
                <span>Limit token to specific groups</span>
              </label>

              {limitToGroups && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <SelectInput
                    name="Add Group"
                    value=""
                    onChange={(selectedId) => {
                      if (
                        selectedId && !selectedGroupIds.includes(selectedId)
                      ) {
                        setSelectedGroupIds([...selectedGroupIds, selectedId]);
                      }
                    }}
                    options={[
                      { id: "", name: "Select group to add..." },
                      ...[...accountGroups]
                        .filter((g) => !selectedGroupIds.includes(g.id))
                        .sort(
                          (a, b) =>
                            (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
                        )
                        .map((g) => ({
                          id: g.id,
                          name: `${g.name} (${
                            formatLastActivity(g.lastActivityAt)
                          })`,
                        })),
                    ]}
                  />

                  {selectedGroupIds.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginTop: 4,
                      }}
                    >
                      {selectedGroupIds.map((id) => {
                        const group = accountGroups.find((g) => g.id === id);
                        const name = group?.name || "Unknown";
                        return (
                          <div
                            key={id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              background: th.cardBgAccent ?? th.accentBg ??
                                th.pageBg,
                              border: `1px solid ${th.border}`,
                              borderRadius: 16,
                              padding: "3px 10px",
                              fontSize: 12,
                              color: th.text,
                            }}
                          >
                            <span>{name}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedGroupIds(
                                  selectedGroupIds.filter((x) => x !== id),
                                )}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: th.danger,
                                cursor: "pointer",
                                fontWeight: "bold",
                                padding: 0,
                                fontSize: 14,
                                lineHeight: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              &times;
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {error && (
        <div style={{ color: th.danger, fontSize: 13, marginBottom: 8 }}>
          {error}
        </div>
      )}
      {loading
        ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "10px 0",
            }}
          >
            <ClipLoader size={20} color={th.textFaint} />
          </div>
        )
        : error
        ? null
        : empty(active)
        ? (
          <div style={{ color: th.textFaint, fontSize: 13 }}>
            No tokens yet. Create one to get started.
          </div>
        )
        : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {active.map((tok) => (
              <TokenRow key={tok.id} t={tok} onRevoke={handleRevoke} />
            ))}
          </ul>
        )}
      {nonempty(revoked) && (
        <details style={{ marginTop: 8 }}>
          <summary
            style={{ color: th.textFaint, fontSize: 12, cursor: "pointer" }}
          >
            {revoked.length} revoked
          </summary>
          <ul style={{ listStyle: "none", padding: 0, margin: "4px 0" }}>
            {revoked.map((tok) => (
              <li
                key={tok.id}
                style={{
                  color: th.textSubtle,
                  fontSize: 12,
                  marginBottom: 2,
                  textDecoration: "line-through",
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <span>{tok.name}</span>
                {tok.groups && tok.groups.length > 0 && (
                  <span style={{ fontSize: 10, opacity: 0.8 }}>
                    ({tok.groups.map((g) =>
                      g.name
                    ).join(", ")})
                  </span>
                )}
                <span style={{ color: th.textFaint, fontSize: 11 }}>
                  ({new Date(tok.revokedAt!).toLocaleDateString()})
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const userIdForEmail = (email: string) =>
  queryOnce({ users: { $: { where: { email } } } })
    .then(({ data: { users } }) =>
      empty(users)
        ? letIn(
          id(),
          (id) => transact(tx.users[id].update({ email })).then(() => id),
        )
        : users[0].id
    );

const createNewAccount = pipe(userIdForEmail, (adminId: string) =>
  transact(
    tx.accounts[id()]
      .update({ secret: crypto.randomUUID() })
      .link({ admins: adminId }),
  ));

const addAdmin = (accountId: string, email: string) =>
  userIdForEmail(email).then((adminId) =>
    transact(tx.accounts[accountId].link({ admins: adminId }))
  );

const removeAdmin = (accountId: string, email: string) =>
  userIdForEmail(email).then((adminId) =>
    transact(tx.accounts[accountId].unlink({ admins: adminId }))
  );

const EmailList = (
  { items, onRemove, onAdd, label }: {
    items: { id: string; email: string }[];
    onRemove: (email: string) => void;
    onAdd: (email: string) => void;
    label: string;
  },
) => {
  const th = t();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const handleAdd = () => {
    if (!email.trim()) return;
    onAdd(email.trim());
    setEmail("");
    setAdding(false);
  };

  const sectionHeaderStyle = {
    fontWeight: 500,
    fontSize: 14,
    color: th.textDim,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  return (
    <div>
      <div style={sectionHeaderStyle}>
        {label}
        <button
          type="button"
          className="compact-button"
          style={{ fontSize: 12, textTransform: "none" }}
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? "Cancel" : "+ Add"}
        </button>
      </div>
      {adding && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className="input-field"
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button
            type="button"
            className="compact-button"
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map(({ id, email }) => (
          <li
            key={id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0",
              borderBottom: `1px solid ${th.border}`,
              fontSize: 14,
            }}
          >
            <span style={{ flex: 1, color: th.text }}>{email}</span>
            <button
              type="button"
              className="compact-button"
              style={{ fontSize: 12, color: th.danger }}
              onClick={() => onRemove(email)}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AccountCard = (
  { account }: {
    account: {
      id: string;
      secret: string;
      admins: { id: string; email: string }[];
      groups?: { id: string; name: string; lastActivityAt?: number }[];
    };
  },
) => {
  const th = t();

  return (
    <div
      style={{
        background: th.cardBg,
        borderRadius: 12,
        padding: 24,
        border: `1px solid ${th.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 20,
        maxWidth: 600,
        width: "100%",
      }}
    >
      <div>
        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            color: th.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          Account ID
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: th.textBright,
            fontFamily: "monospace",
            wordBreak: "break-all",
          }}
        >
          {account.id}
        </div>
      </div>
      <TokenManager
        accountSecret={account.secret}
        accountGroups={account.groups || []}
      />
      <EmailList
        items={account.admins}
        onRemove={(email) => removeAdmin(account.id, email)}
        onAdd={(email) => addAdmin(account.id, coerce(email))}
        label="Admins"
      />
    </div>
  );
};

export const AdminPanel = () => {
  const th = t();
  const ownEmail = useAuth().user?.email;
  const { isLoading, error, data } = useQuery({
    accounts: { admins: {}, groups: {} },
  });
  if (isLoading) return <Loading text="Loading accounts" fullPage />;
  if (error) return <ErrorMessage error={error} />;
  return (
    <div
      style={{
        padding: 24,
        background: th.pageBg,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
        color: th.text,
      }}
    >
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          color: th.textBright,
          margin: 0,
        }}
      >
        Accounts
      </h1>
      {empty(data.accounts)
        ? (
          <div
            style={{
              textAlign: "center",
              color: th.textDim,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
              marginTop: 40,
            }}
          >
            <div style={{ fontSize: 16 }}>
              Create an account to get started.
            </div>
            {ownEmail && (
              <button
                type="button"
                className="compact-button"
                style={{ fontSize: 16, padding: "8px 24px" }}
                onClick={() => createNewAccount(ownEmail)}
              >
                Create account
              </button>
            )}
          </div>
        )
        : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              width: "100%",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: th.textDim,
                margin: "0 0 -8px 0",
                width: "100%",
                maxWidth: 600,
                textAlign: "left",
              }}
            >
              Your Accounts
            </h2>
            {data.accounts.map((account) => (
              <AccountCard key={account.id} account={account} />
            ))}
          </div>
        )}
    </div>
  );
};
